import { Directive, ElementRef, Input, Renderer2, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

/**
 * Internal service for loading SVG content.
 * @internal
 */
@Injectable({
  providedIn: 'root',
})
class SvgLoaderService {
  /** In-memory cache to avoid duplicate HTTP requests for the same SVG URL */
  private cache = new Map<string, Observable<string>>();

  constructor(private http: HttpClient) {}

  getSvg(path: string): Observable<string> {
    const cached = this.cache.get(path);
    if (cached) {
      return cached;
    }

    const svg$ = this.http.get(path, { responseType: 'text' }).pipe(
      shareReplay(1),
      catchError((error) => {
        this.cache.delete(path);
        console.error('Error loading SVG:', error);
        throw error;
      })
    );

    this.cache.set(path, svg$);

    return svg$;
  }
}

/**
 * Angular directive that replaces IMG elements with inline SVG content.
 *
 * This directive loads SVG files and replaces the host IMG element with the actual SVG DOM,
 * enabling CSS styling, animations, and scriptable access to SVG internals.
 *
 * Features:
 * - Browser-native HTTP caching
 * - Preserves IMG attributes on the SVG element
 * - Optional cache busting
 * - Graceful error handling with fallback
 *
 * @example
 * ```html
 * <!-- Basic usage -->
 * <img sgInlineSvg="assets/icons/star.svg"
 *      alt="Star icon"
 *      class="icon icon-star">
 *
 * <!-- With cache busting -->
 * <img sgInlineSvg="assets/icons/user.svg"
 *      [sgSvgCacheBust]="true"
 *      alt="User icon">
 *
 * <!-- All IMG attributes are preserved on the SVG -->
 * <img sgInlineSvg="assets/logo.svg"
 *      class="logo"
 *      width="200"
 *      height="100"
 *      title="Company Logo">
 * ```
 *
 * @selector [sgInlineSvg]
 */
@Directive({
  selector: '[sgInlineSvg]',
  standalone: false,
})
export class InlineSvgDirective {
  /**
   * The path or URL to the SVG file to load and inline.
   * Can be a relative path, absolute path, or full URL.
   */
  @Input('sgInlineSvg') set svgPath(path: string) {
    this._svgPath = path;
    if (this.svgPath) {
      this.loadAndInlineSvg();
    }
  }

  get svgPath(): string {
    return this._svgPath;
  }

  private _svgPath: string = '';

  /**
   * When true, forces a fresh download by adding a cache-busting parameter.
   * Useful during development or when you know the SVG has been updated.
   * @default false
   */
  @Input('sgSvgCacheBust') cacheBust: boolean = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private svgLoader: SvgLoaderService
  ) {}

  private loadAndInlineSvg() {
    // Add cache busting parameter if enabled
    const url = this.cacheBust ? `${this.svgPath}?cb=${Date.now()}` : this.svgPath;

    this.svgLoader.getSvg(url).subscribe({
      next: (svgContent) => {
        this.replaceWithInlineSvg(svgContent);
      },
      error: (error) => {
        console.error('Error loading SVG:', error);
        // Optionally show a fallback or placeholder
        this.showFallback();
      },
    });
  }

  private showFallback() {
    const imgElement = this.el.nativeElement;
    if (!imgElement) {
      console.warn('Image element not found');
      return;
    }
    imgElement.alt = imgElement.alt || 'SVG could not be loaded';
    imgElement.style.display = 'none';
  }

  private replaceWithInlineSvg(svgContent: string) {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgElement = svgDoc.querySelector('svg');

    if (!svgElement) {
      console.warn('Invalid SVG content provided');
      return;
    }

    const imgElement = this.el.nativeElement;
    const parentNode = imgElement.parentNode;

    if (!parentNode) {
      console.warn('Image element has no parent node');
      return;
    }

    // Copy attributes from img to svg (excluding src and directive attribute)
    Array.from(imgElement.attributes as NamedNodeMap)
      .filter((attr: Attr) => !['src', 'sginlinesvg'].includes(attr.name))
      .forEach((attr: Attr) => svgElement.setAttribute(attr.name, attr.value));

    // Set SVG display style
    svgElement.style.display = 'inline-block';

    // Remove any existing SVG to prevent duplicates
    const existingSvg = parentNode.querySelector('svg');
    if (existingSvg) {
      this.renderer.removeChild(parentNode, existingSvg);
    }

    // Replace img with svg
    this.renderer.insertBefore(parentNode, svgElement, imgElement);
    imgElement.style.display = 'none'; // Hide the original img element
  }
}
